import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';

/** Material UI Packages. */
import { Box, Select, FormControl, InputLabel, MenuItem, IconButton } from '@mui/material';

/** Material UI Icons. */
import CloseIcon from '@mui/icons-material/Close';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setFormData, resetFormData } from 'shared/store/slices/filterViewSlice';
import { deleteFilter } from 'shared/store/slices/filterViewListSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import ThemeButton from 'shared/ui/Button';
import { deleteSocketData } from 'shared/lib/fetchSocket';

import FilterViewDialog from './FilterViewDialog';

export default function FilterView() {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** All list of filter views received from the server. */
    const filterViewList = useSelector((state) => state.filterViewList);
    /** State to manage the filter values applied to the current DataGrid. */
    const filterView = useSelector((state) => state.filterViewData);

    /** State for select component value currently.  */
    const [selectedValue, setSelectedValue] = useState('');
    /** State to manage the dialog's open/close state. */
    const [open, setOpen] = useState(false);

    /** Memoized filtered items based on shipment type.*/
    const filterItems = useMemo(() => {
        return filterViewList.filter((item) => item.shipment_type === tabValue);
    }, [filterViewList, tabValue]);

    /** Hook to reset the selectValue when the quickfilter value is changed through a todo card.  */
    useEffect(() => {
        if (filterView.id == '') {
            setSelectedValue('');
        }
    }, [filterView]);

    /** Handle to change select value.*/
    const handleFilterChange = ({ target: { value } }) => {
        const selectedItem = filterViewList.find((item) => item.id === value);

        if (selectedItem) {
            setSelectedValue(value);
            dispatch(setFormData(selectedItem));
        } else {
            setSelectedValue('');
            dispatch(resetFormData());
        }
    };

    /** Deletes the selected item, ensuring the default cannot be deleted. */
    const handleFilterDelete = (id) => {
        if (!window.confirm('Are you sure you want to delete the filter view?')) {
            return;
        }

        deleteSocketData(dispatch, tabValue, 'row', 'il_filter', 'deleteFilter', id, () => {
            dispatch(deleteFilter(id));
            if (selectedValue === id) {
                setSelectedValue('');
                dispatch(resetFormData());
            }
            dispatch(setSnackbar({ open: true, message: 'Successfully deleted.', severity: 'success' }));
        });
    };

    /** Handle to open a dialog. */
    const handleClickOpen = () => setOpen(true);

    return (
        <Box>
            <FormControl
                sx={{
                    minWidth: 170,
                    mr: '9px',
                    '& .MuiSelect-select': { padding: '6px 6px' },
                    '& .MuiInputBase-root': { height: '30px' },
                }}
                size="small"
            >
                <InputLabel id="filterId" sx={{ fontSize: 13 }}>
                    Filter
                </InputLabel>
                <Select
                    labelId="filterId"
                    value={selectedValue || ''}
                    label="Filter"
                    onChange={handleFilterChange}
                    sx={{
                        fontSize: 12,
                        height: '30px',
                    }}
                >
                    <MenuItem value="" sx={{ fontSize: 12 }}>
                        <em>None</em>
                    </MenuItem>
                    {filterItems.map((item) => (
                        <MenuItem
                            key={item.id}
                            value={item.id}
                            name="filterView"
                            sx={{
                                fontSize: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            {item.name}
                            <IconButton
                                size="small"
                                onClick={(event) => {
                                    event.stopPropagation(); // Prevent event bubbling
                                    handleFilterDelete(item.id);
                                }}
                                sx={{
                                    ml: 1,
                                    p: '2px',
                                    display: filterView.id === item.id ? 'none' : 'flex',
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ThemeButton text="CREATE" width={'68px'} onClick={handleClickOpen} />
            <FilterViewDialog open={open} setOpen={setOpen} setSelectedValue={setSelectedValue} />
        </Box>
    );
}
